import { HubSelect } from "@/components/HubSelect";
import { PageHeader } from "@bednbody/ui/components/page-header";
import { Button } from "@bednbody/ui/components/button";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { StatTile } from "@bednbody/ui/components/stat-tile";
import { ToggleGroup, ToggleGroupItem } from "@bednbody/ui/components/toggle-group";
import { RetryButton } from "@bednbody/ui/components/retry-button";
import {
  BuildingsIcon,
  CalendarCheckIcon,
  DownloadSimpleIcon,
  MedalIcon,
  WalletIcon,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useHubsList } from "../ops-dashboard/ops-dashboard.hooks";
import { AnalyticsCharts } from "./AnalyticsCharts";
import type { AnalyticsSummary } from "./analytics.hooks";
import { summaryToCsv, useAnalyticsSummary } from "./analytics.hooks";

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
] as const;

const pesos = new Intl.NumberFormat("en-PH", {
  style: "currency",
  currency: "PHP",
  maximumFractionDigits: 0,
});

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function MetricCard({
  icon,
  label,
  value,
  detail,
}: {
  icon: Icon;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <StatTile detail={detail} icon={icon} label={label} layout="compact">
      {value}
    </StatTile>
  );
}

function Insight({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-sm font-semibold text-foreground">{value}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>
    </div>
  );
}

function RankingList({
  title,
  subtitle,
  items,
  emptyMessage,
}: {
  title: string;
  subtitle: string;
  items: { name: string; bookings: number; revenue: number }[];
  emptyMessage: string;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-xs">
      <header className="border-b border-border px-5 py-4">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
      </header>
      {items.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <ol className="divide-y divide-border">
          {items.slice(0, 5).map((item, index) => (
            <li className="flex items-center gap-3 px-5 py-3.5" key={item.name}>
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-muted-foreground">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{item.name}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {item.bookings} completed session{item.bookings === 1 ? "" : "s"}
                </p>
              </div>
              <p className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                {pesos.format(item.revenue)}
              </p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function ExecutiveBriefing({ data }: { data: AnalyticsSummary }) {
  const leadingHub = data.hubPerformance[0];
  const leadingTherapist = data.topTherapists[0];
  const leadingPayment = data.paymentBreakdown[0];

  return (
    <section className="grid gap-5 rounded-2xl border border-primary/20 bg-primary/[0.035] px-5 py-4 sm:grid-cols-3">
      <Insight
        label="Leading hub"
        value={leadingHub?.name ?? "No activity yet"}
        detail={
          leadingHub
            ? `${leadingHub.bookings} bookings · ${pesos.format(leadingHub.revenue)} delivered`
            : "No bookings in this range"
        }
      />
      <Insight
        label="Top therapist"
        value={leadingTherapist?.name ?? "No completed sessions"}
        detail={
          leadingTherapist
            ? `${leadingTherapist.bookings} sessions · ${pesos.format(leadingTherapist.revenue)}`
            : "No delivery data in this range"
        }
      />
      <Insight
        label="Preferred payment"
        value={leadingPayment?.method ?? "No payment data"}
        detail={
          leadingPayment
            ? `${leadingPayment.percentage}% of non-cancelled bookings`
            : "No bookings in this range"
        }
      />
    </section>
  );
}

export function AnalyticsPage() {
  const { claims } = useAuth();
  const [selectedHubId, setSelectedHubId] = useState<string | null>(null);
  const [days, setDays] = useState<number>(30);
  const { data: hubs } = useHubsList();
  const hubScoped = claims?.role === "hub_manager";
  const hubId = hubScoped ? (claims?.hub_id ?? null) : selectedHubId;
  const { data: analyticsData, isLoading, isError, refetch } = useAnalyticsSummary(hubId, days);

  return (
    <div className="w-full space-y-8">
      <PageHeader
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ToggleGroup
              aria-label="Analytics range"
              size="sm"
              spacing={1}
              value={[String(days)]}
              onValueChange={(values) => {
                // Array-valued like every ToggleGroup; `undefined` means the
                // group emptied itself, which must not mean "range 0".
                const next = values.at(-1);
                if (next !== undefined) setDays(Number(next));
              }}
            >
              {RANGES.map((range) => (
                <ToggleGroupItem key={range.days} value={String(range.days)}>
                  {range.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            {!hubScoped && (
              <HubSelect
                ariaLabel="Hub scope"
                className="w-48"
                hubs={hubs ?? []}
                variant="combobox"
                value={selectedHubId}
                onValueChange={setSelectedHubId}
              />
            )}
            <Button
              variant="default"
              size="sm"
              disabled={!analyticsData}
              onClick={() =>
                analyticsData &&
                downloadCsv(
                  summaryToCsv(analyticsData),
                  `bednbody-analytics-${days}d-${new Date().toISOString().slice(0, 10)}.csv`,
                )
              }
              type="button"
            >
              <DownloadSimpleIcon />
              Export
            </Button>
          </div>
        }
        description="A focused view of booking demand, delivery value, and the teams driving it."
        title="Performance overview"
      />

      {isError ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          <p>Couldn&apos;t load analytics. Check your connection and try again.</p>
          <RetryButton className="mt-3" size="sm" onRetry={() => refetch()}>
            Try again
          </RetryButton>
        </div>
      ) : null}

      {isLoading ? (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              // h-24, not the old h-32: the compact tile is ~94px tall, and a
              // 128px placeholder made the row jump when the data landed.
              <Skeleton className="h-24 motion-reduce:animate-none" key={index} />
            ))}
          </div>
          <Skeleton className="h-80 motion-reduce:animate-none" />
        </>
      ) : null}

      {analyticsData ? (
        <>
          <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              detail="Non-cancelled in range"
              icon={CalendarCheckIcon}
              label="Bookings"
              value={analyticsData.totalBookings.toLocaleString()}
            />
            <MetricCard
              detail="Completed sessions only"
              icon={WalletIcon}
              label="Delivered value"
              value={pesos.format(analyticsData.totalRevenue)}
            />
            <MetricCard
              detail="Across non-cancelled bookings"
              icon={MedalIcon}
              label="Average booking value"
              value={pesos.format(analyticsData.averageBookingValue)}
            />
            <MetricCard
              detail={hubId ? "Within this selected hub" : "With bookings in range"}
              icon={BuildingsIcon}
              label="Active hubs"
              value={analyticsData.hubPerformance.length.toLocaleString()}
            />
          </section>

          <ExecutiveBriefing data={analyticsData} />

          <AnalyticsCharts data={analyticsData} />

          <section className="grid gap-5 lg:grid-cols-2">
            <RankingList
              emptyMessage="No completed sessions in this range."
              items={analyticsData.topTherapists}
              subtitle="Ranked by delivered value"
              title="Therapist performance"
            />
            <RankingList
              emptyMessage="No hub activity in this range."
              items={analyticsData.hubPerformance}
              subtitle="Ranked by delivered value"
              title="Hub performance"
            />
          </section>

          <p className="px-1 text-xs text-muted-foreground">
            Delivered value reflects completed sessions. It is operational reporting, not the
            settled-payment ledger.
          </p>
        </>
      ) : null}
    </div>
  );
}
